'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import type { SignupIntent, SignupRole } from '@/lib/types';
import { HubSpotForm } from '@/components/ui/HubSpotForm';
import type { HubSpotFormMessages } from '@/components/ui/HubSpotForm';

type Choice = SignupRole | 'both';

interface RoleOption {
  choice: Choice;
  title: string;
  detail: string;
}

// Approved canideal.org wording (sales before buying).
const ROLE_OPTIONS: RoleOption[] = [
  { choice: 'vendor', title: 'Sell products', detail: 'Register as a seller.' },
  { choice: 'retailer', title: 'Source products', detail: 'Register as a buyer.' },
  { choice: 'both', title: 'Both', detail: 'Complete both onboarding paths.' },
];

const FORM_MESSAGES: HubSpotFormMessages = {
  loading: 'Loading your signup form…',
  ready: '',
  timeout: 'The signup form couldn’t load. Please try again.',
  error: 'The signup form couldn’t load. Please try again.',
};

function copyFor(choice: Choice | null) {
  if (!choice)
    return {
      title: 'Register with CanIDeal',
      intro: 'Choose sales, buying, or both to get started.',
    };
  if (choice === 'vendor')
    return { title: 'Register as a seller', intro: 'Tell us what you can supply.' };
  return {
    title: 'Register as a buyer',
    intro:
      choice === 'both'
        ? 'Start with your buyer registration. After submitting, return to the role choices and select Sell products to complete your supplier profile.'
        : 'Define your buying criteria.',
  };
}

const isIntent = (value: string | undefined): value is SignupIntent =>
  value === 'vendor' || value === 'retailer' || value === 'choose';

/**
 * Global registration dialog. Any element with `data-cid-signup` opens it
 * (plain clicks only); the element's href (signup.ts) stays the fallback for
 * new-tab clicks and no-JS visitors.
 */
export function SignupDialog() {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  const rolesRef = useRef<HTMLDivElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const scrollState = useRef<{ x: number; y: number; body: string; html: string } | null>(null);
  const [choice, setChoice] = useState<Choice | null>(null);
  const [open, setOpen] = useState(false);
  const pathname = usePathname();

  const focusFirstRole = () =>
    rolesRef.current?.querySelector('button')?.focus({ preventScroll: true });

  const openWith = useCallback((intent: SignupIntent, trigger: HTMLElement) => {
    const dialog = dialogRef.current;
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    if (!dialog.open) {
      returnFocus.current = trigger;
      // Reopened before the previous `close` event ran: keep the original
      // saved values (re-reading now would capture our own "hidden").
      scrollState.current ??= {
        x: window.scrollX,
        y: window.scrollY,
        body: document.body.style.overflow,
        html: document.documentElement.style.overflow,
      };
      dialog.showModal();
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    }
    setOpen(true);
    setChoice(intent === 'choose' ? null : intent);
    return true;
  }, []);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const trigger = (event.target as Element | null)?.closest<HTMLElement>('[data-cid-signup]');
      const intent = trigger?.dataset.cidSignup;
      if (!trigger || !isIntent(intent)) return;
      if (openWith(intent, trigger)) event.preventDefault();
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [openWith]);

  useEffect(() => {
    if (!open) return;
    if (choice) backRef.current?.focus({ preventScroll: true });
    else focusFirstRole();
  }, [open, choice]);

  const close = () => dialogRef.current?.close();

  /** Undo the scroll lock exactly once (close event, navigation or unmount). */
  const releaseScroll = useCallback((restorePosition: boolean) => {
    const saved = scrollState.current;
    if (!saved) return;
    scrollState.current = null;
    document.body.style.overflow = saved.body;
    document.documentElement.style.overflow = saved.html;
    if (restorePosition) window.scrollTo(saved.x, saved.y);
  }, []);

  const onClose = () => {
    // `close` fires asynchronously: ignore a stale event if it was reopened since.
    if (dialogRef.current?.open) return;
    setOpen(false);
    setChoice(null);
    releaseScroll(true);
    if (returnFocus.current?.isConnected) returnFocus.current.focus({ preventScroll: true });
    returnFocus.current = null;
  };

  // Client-side navigation (e.g. browser Back) while open: close it so the
  // next page is not left behind a modal with scrolling locked.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog?.open) return;
    releaseScroll(false); // the new page keeps its own scroll position
    dialog.close();
  }, [pathname, releaseScroll]);

  // Never leave the page scroll-locked if the dialog unmounts while open.
  useEffect(() => () => releaseScroll(false), [releaseScroll]);

  const onBackdropClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    const dialog = dialogRef.current;
    if (!dialog || event.target !== dialog) return;
    const box = dialog.getBoundingClientRect();
    const outside =
      event.clientX < box.left ||
      event.clientX > box.right ||
      event.clientY < box.top ||
      event.clientY > box.bottom;
    if (outside) close();
  };

  const copy = copyFor(choice);
  const formRole: SignupRole | null = choice === 'both' ? 'retailer' : choice;

  return (
    // The native <dialog> handles Escape (cancel) and focus trapping; the
    // backdrop click mirrors the approved canideal.org behaviour.

    <dialog
      ref={dialogRef}
      id="cid-signup-dialog"
      className="cid-signup-dialog"
      aria-labelledby="cid-signup-title"
      aria-describedby="cid-signup-intro"
      onClose={onClose}
      onClick={onBackdropClick}
    >
      <div className="cid-signup-top">
        <div>
          <p className="cid-signup-eyebrow">GET STARTED</p>
          <h2 id="cid-signup-title">{copy.title}</h2>
        </div>
        <button
          type="button"
          className="cid-signup-close"
          aria-label="Close registration"
          onClick={close}
        >
          ×
        </button>
      </div>
      <p id="cid-signup-intro" className="cid-signup-intro">
        {copy.intro}
      </p>
      {open && !formRole && (
        <div ref={rolesRef} className="cid-signup-roles">
          {ROLE_OPTIONS.map((option) => (
            <button
              key={option.choice}
              type="button"
              data-role={option.choice}
              onClick={() => setChoice(option.choice)}
            >
              <strong>{option.title}</strong>
              <span>{option.detail}</span>
            </button>
          ))}
        </div>
      )}
      {open && formRole && (
        <>
          <button
            ref={backRef}
            type="button"
            className="cid-signup-back"
            onClick={() => setChoice(null)}
          >
            ← Choose another role
          </button>
          <HubSpotForm
            key={`${choice}`}
            form={formRole}
            messages={FORM_MESSAGES}
            className="cid-signup-form"
          />
        </>
      )}
    </dialog>
  );
}
